'use client'

import { useState, useEffect, useCallback } from 'react'
import Header from '@/components/header'
import { ArrowLeft, Copy, RefreshCw, Wallet, Key, ChevronDown, ChevronUp, CheckCircle, Gift } from 'lucide-react'
import { useRouter } from 'next/navigation'
import Api from '@/api/h5Api'

interface CcInfo {
  ccBalance: number
  token: string
  freeCallCount: number
  freeDailyQuota: number
}

interface ApiResponse<T = any> {
  code: number
  message?: string
  data: T
}

export default function Page() {
  const router = useRouter()
  const [ccInfo, setCcInfo] = useState<CcInfo | null>(null)
  const [loading, setLoading] = useState(false)
  const [copied, setCopied] = useState(false)
  const [showGuide, setShowGuide] = useState(false)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [isGuest, setIsGuest] = useState(false)

  // 检查登录状态
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('token')
      setIsLoggedIn(!!token)
      setIsGuest(localStorage.getItem('isGuest') === '1')
    }
  }, [])

  // 获取 CC 余额和 apikey
  const fetchCcInfo = useCallback(async () => {
    setLoading(true)
    try {
      const res = (await (Api as any).userGetCcInfo({})) as unknown as ApiResponse<CcInfo>
      if (res.code === 200) {
        // 没有 API Key 时自动创建
        if (!res.data.token) {
          await (Api as any).userCreateToken({})
          // 重新获取，拿到刚创建的 token
          const res2 = (await (Api as any).userGetCcInfo({})) as unknown as ApiResponse<CcInfo>
          if (res2.code === 200) {
            setCcInfo(res2.data)
            return
          }
        }
        setCcInfo(res.data)
      }
    } catch (error) {
      console.error('获取CC信息失败:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (isLoggedIn) {
      fetchCcInfo()
    }
  }, [isLoggedIn, fetchCcInfo])

  // 复制到剪贴板
  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // fallback
      const el = document.createElement('textarea')
      el.value = text
      document.body.appendChild(el)
      el.select()
      document.execCommand('copy')
      document.body.removeChild(el)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const balanceColor =
    !ccInfo
      ? 'text-muted-foreground'
      : ccInfo.ccBalance <= 0
      ? 'text-destructive'
      : ccInfo.ccBalance < 1
      ? 'text-amber-500'
      : 'text-green-500'

  return (
    <>
      <Header />
      <main className="min-h-screen bg-linear-to-br from-primary/5 via-background to-secondary/5 relative overflow-hidden">
        {/* 背景装饰 */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl animate-pulse-slow" />
          <div
            className="absolute bottom-20 right-1/4 w-96 h-96 bg-secondary/5 rounded-full blur-3xl animate-pulse-slow"
            style={{ animationDelay: '2s' }}
          />
        </div>

        <div className="relative max-w-2xl mx-auto px-4 py-12">
          {/* 返回按钮 */}
          <button
            onClick={() => router.push('/light')}
            className="group mb-8 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-card/60 backdrop-blur-xl border border-border/50 hover:border-primary/50 shadow-sm hover:shadow-md transition-all duration-300"
          >
            <ArrowLeft className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors duration-300 group-hover:-translate-x-0.5" />
            <span className="text-sm font-medium text-muted-foreground group-hover:text-foreground transition-colors duration-300">
              返回首页
            </span>
          </button>

          {/* 页面标题 */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold bg-linear-to-r from-primary to-secondary bg-clip-text text-transparent">
              Claude Code 控制台
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              管理您的 API Key 和余额，接入 Claude Code 插件
            </p>
          </div>

          {!isLoggedIn ? (
            /* 未登录提示 */
            <div className="bg-card/80 backdrop-blur-xl rounded-2xl border-2 border-border p-8 text-center">
              <p className="text-muted-foreground mb-4">请先登录以查看您的余额和 API Key</p>
              <button
                onClick={() => router.push('/light/login')}
                className="px-6 py-2.5 rounded-xl bg-linear-to-r from-primary to-secondary text-primary-foreground font-semibold hover:opacity-90 transition-opacity"
              >
                去登录
              </button>
            </div>
          ) : isGuest ? (
            /* 游客提示 */
            <div className="bg-card/80 backdrop-blur-xl rounded-2xl border-2 border-amber-300/50 p-8 text-center space-y-4">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-amber-100 dark:bg-amber-900/30 mb-2">
                <Key className="w-7 h-7 text-amber-500" />
              </div>
              <h2 className="text-lg font-semibold text-foreground">游客无法使用此功能</h2>
              <p className="text-sm text-muted-foreground">
                生成 API Key 需要注册账号。游客身份无法创建或查看 API Key，请先注册登录。
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
                <button
                  onClick={() => router.push('/light/register')}
                  className="px-6 py-2.5 rounded-xl bg-linear-to-r from-primary to-secondary text-primary-foreground font-semibold hover:opacity-90 transition-opacity"
                >
                  注册账号
                </button>
                <button
                  onClick={() => router.push('/light/login')}
                  className="px-6 py-2.5 rounded-xl bg-card border-2 border-border hover:border-primary/50 font-semibold transition-all duration-300 text-foreground"
                >
                  登录账号
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* 余额卡片 */}
              <div className="bg-card/80 backdrop-blur-xl rounded-2xl border-2 border-border hover:border-primary/30 transition-colors duration-300 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Wallet className="w-4 h-4 text-primary" />
                    </div>
                    <span className="font-semibold text-foreground">账户余额</span>
                  </div>
                  <button
                    onClick={fetchCcInfo}
                    disabled={loading}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-muted/50 hover:bg-muted text-xs text-muted-foreground hover:text-foreground transition-all duration-200 disabled:opacity-50"
                  >
                    <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
                    刷新
                  </button>
                </div>

                {loading && !ccInfo ? (
                  <div className="h-14 flex items-center">
                    <div className="w-32 h-8 bg-muted/50 rounded-lg animate-pulse" />
                  </div>
                ) : (
                  <div className="flex items-end gap-2">
                    <span className={`text-4xl font-bold tabular-nums ${balanceColor}`}>
                      ¥{ccInfo ? ccInfo.ccBalance.toFixed(4) : '0.0000'}
                    </span>
                    <span className="text-sm text-muted-foreground mb-1">人民币</span>
                  </div>
                )}

                {ccInfo && ccInfo.ccBalance <= 0 && (
                  <p className="text-xs text-destructive mt-2">
                    余额不足，每日可免费使用 {ccInfo.freeDailyQuota} 次，或联系管理员充值：xu1183391880
                  </p>
                )}
                {ccInfo && ccInfo.ccBalance > 0 && ccInfo.ccBalance < 1 && (
                  <p className="text-xs text-amber-500 mt-2">
                    余额较低，请及时联系管理员充值，微信：xu1183391880
                  </p>
                )}
              </div>

              {/* 免费次数卡片（余额为0时显示） */}
              {ccInfo && ccInfo.ccBalance <= 0 && (
                <div className="bg-card/80 backdrop-blur-xl rounded-2xl border-2 border-border hover:border-primary/30 transition-colors duration-300 p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Gift className="w-4 h-4 text-primary" />
                    </div>
                    <span className="font-semibold text-foreground">今日免费次数</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 bg-muted/40 rounded-full h-2.5 overflow-hidden">
                      <div
                        className="h-full bg-linear-to-r from-primary to-secondary rounded-full transition-all duration-500"
                        style={{
                          width: `${Math.min(100, (ccInfo.freeCallCount / ccInfo.freeDailyQuota) * 100)}%`,
                        }}
                      />
                    </div>
                    <span className={`text-sm font-semibold tabular-nums ${ccInfo.freeCallCount >= ccInfo.freeDailyQuota ? 'text-destructive' : 'text-foreground'}`}>
                      {ccInfo.freeCallCount} / {ccInfo.freeDailyQuota}
                    </span>
                  </div>
                  {ccInfo.freeCallCount >= ccInfo.freeDailyQuota ? (
                    <p className="text-xs text-destructive mt-2">今日免费次数已用完，请联系管理员充值，微信：xu1183391880</p>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-2">余额为 0 时每日可免费调用 {ccInfo.freeDailyQuota} 次，次日自动重置</p>
                  )}
                </div>
              )}

              {/* API Key 卡片 */}
              <div className="bg-card/80 backdrop-blur-xl rounded-2xl border-2 border-border hover:border-primary/30 transition-colors duration-300 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Key className="w-4 h-4 text-primary" />
                  </div>
                  <span className="font-semibold text-foreground">API Key</span>
                </div>

                {loading && !ccInfo ? (
                  <div className="w-full h-10 bg-muted/50 rounded-lg animate-pulse" />
                ) : (
                  <div
                    className="flex items-center gap-3 p-3 rounded-xl bg-muted/30 border border-border cursor-pointer hover:border-primary/50 hover:bg-muted/50 transition-all duration-200 group"
                    onClick={() => ccInfo?.token && handleCopy(ccInfo.token)}
                    title="点击复制"
                  >
                    <code className="flex-1 text-sm font-mono text-foreground truncate">
                      {ccInfo?.token || '暂无 API Key'}
                    </code>
                    <div className="flex-shrink-0">
                      {copied ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <Copy className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                      )}
                    </div>
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-2">点击复制，配置到 Claude Code 插件的 ANTHROPIC_AUTH_TOKEN 环境变量</p>
              </div>

              {/* 接入指南 */}
              <div className="bg-card/80 backdrop-blur-xl rounded-2xl border-2 border-border p-6">
                <button
                  className="w-full flex items-center justify-between"
                  onClick={() => setShowGuide(!showGuide)}
                >
                  <span className="font-semibold text-foreground">接入指南</span>
                  {showGuide ? (
                    <ChevronUp className="w-4 h-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  )}
                </button>

                {showGuide && (
                  <div className="mt-4 space-y-4 text-sm text-muted-foreground">
                    <div className="space-y-2">
                      <p className="font-medium text-foreground">① 安装 Claude Code</p>
                      <div className="bg-muted/40 rounded-lg px-4 py-3 font-mono text-xs">
                        npm install -g @anthropic-ai/claude-code
                      </div>
                    </div>

                    <div className="space-y-2">
                      <p className="font-medium text-foreground">② 配置环境变量</p>
                      <div className="bg-muted/40 rounded-lg px-4 py-3 font-mono text-xs space-y-1">
                        <div
                          className="cursor-pointer hover:text-foreground transition-colors"
                          onClick={() => ccInfo?.token && handleCopy(`export ANTHROPIC_AUTH_TOKEN=${ccInfo.token}`)}
                          title="点击复制"
                        >
                          export ANTHROPIC_AUTH_TOKEN=<span className="text-primary">{ccInfo?.token || 'your-api-key'}</span>
                        </div>
                        <div
                          className="cursor-pointer hover:text-foreground transition-colors"
                          onClick={() => handleCopy('export ANTHROPIC_BASE_URL=https://chat.xutongbao.top/api/light/chat/claudeCode')}
                          title="点击复制"
                        >
                          export ANTHROPIC_BASE_URL=<span className="text-primary">https://chat.xutongbao.top/api/light/chat/claudeCode</span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <p className="font-medium text-foreground">③ 启动 Claude Code</p>
                      <div className="bg-muted/40 rounded-lg px-4 py-3 font-mono text-xs">
                        claude
                      </div>
                    </div>

                    <div className="space-y-2">
                      <p className="font-medium text-foreground">④ 参考教程</p>
                      <a
                        href="https://blog.csdn.net/xutongbao/article/details/155063974"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline break-all"
                      >
                        https://blog.csdn.net/xutongbao/article/details/155063974
                      </a>
                    </div>

                    <div className="pt-2 border-t border-border/50">
                      <p className="text-xs">
                        计费按官方价格折算，汇率 1 USD = 6.8 CNY。余额为 0 时每日有 5 次免费额度。
                      </p>
                      <p className="text-xs mt-1">
                        客服微信：<span className="text-primary font-medium">xu1183391880</span>
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  )
}
